import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { FacebookPixel } from "@/components/FacebookPixel";
import { AIAssistant } from "@/components/AIAssistant";
import { InactivityLogoutProvider } from "@/components/InactivityLogoutProvider";
import { CookieConsent } from "@/components/CookieConsent";
import { GeoBlock } from "@/components/GeoBlock";

// Eager load the most critical pages
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import OverOns from "./pages/OverOns";
import Disclaimer from "./pages/Disclaimer";
import { AdSenseLoader } from "@/components/AdSenseLoader";

// Lazy load all other pages for better performance
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminRecovery = lazy(() => import("./pages/AdminRecovery"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AllRijscholen = lazy(() => import("./pages/AllRijscholen"));
const RijscholenKaart = lazy(() => import("./pages/RijscholenKaart"));
const RijschoolDetail = lazy(() => import("./pages/RijschoolDetail"));
const RijschoolAanmelden = lazy(() => import("./pages/RijschoolAanmelden"));
const RijschoolDashboard = lazy(() => import("./pages/RijschoolDashboard"));
const RijschoolLeads = lazy(() => import("./pages/RijschoolLeads"));
const RegistratieSucces = lazy(() => import("./pages/RegistratieSucces"));
const OnlineTheorie = lazy(() => import("./pages/OnlineTheorie"));
const TheorieBoeken = lazy(() => import("./pages/TheorieBoeken"));
const Videos = lazy(() => import("./pages/Videos"));
const Reviews = lazy(() => import("./pages/Reviews"));
const Blog = lazy(() => import("./pages/Blog"));
const Nieuws = lazy(() => import("./pages/Nieuws"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const LeerlingAanmelden = lazy(() => import("./pages/LeerlingAanmelden"));
const LeerlingDashboard = lazy(() => import("./pages/LeerlingDashboard"));
const LeadConfirmation = lazy(() => import("./pages/LeadConfirmation"));
const LeadDistribution = lazy(() => import("./pages/LeadDistribution"));
const KolbTest = lazy(() => import("./pages/KolbTest"));
const CBR = lazy(() => import("./pages/CBR"));
const Partners = lazy(() => import("./pages/Partners"));
const PartnerAuth = lazy(() => import("./pages/PartnerAuth"));
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard"));
const PaymentConfirmation = lazy(() => import("./pages/PaymentConfirmation"));
const SubscriptionUpgrade = lazy(() => import("./pages/SubscriptionUpgrade"));
const Verenigingen = lazy(() => import("./pages/Verenigingen"));
const VerenigingAanvragen = lazy(() => import("./pages/VerenigingAanvragen"));
const VerenigingAuth = lazy(() => import("./pages/VerenigingAuth"));
const VerenigingDashboard = lazy(() => import("./pages/VerenigingDashboard"));
const VerenigingBetaaldeVermelding = lazy(() => import("./pages/VerenigingBetaaldeVermelding"));
const Code95Opleiders = lazy(() => import("./pages/Code95Opleiders"));
const Code95OpleiderDetail = lazy(() => import("./pages/Code95OpleiderDetail"));
const Code95Aanmelden = lazy(() => import("./pages/Code95Aanmelden"));
const Code95Login = lazy(() => import("./pages/Code95Login"));
const Code95OpleiderDashboard = lazy(() => import("./pages/Code95OpleiderDashboard"));
const Code95ResetPassword = lazy(() => import("./pages/Code95ResetPassword"));
const RijInstructeur = lazy(() => import("./pages/RijInstructeur"));
const CBRExaminator = lazy(() => import("./pages/CBRExaminator"));
const MedewerkerTheorie = lazy(() => import("./pages/MedewerkerTheorie"));
const VerhaalBram = lazy(() => import("./pages/VerhaalBram"));
const VerhaalNicolas = lazy(() => import("./pages/VerhaalNicolas"));
const VerhaalEva = lazy(() => import("./pages/VerhaalEva"));
const VerhaalCBR2026 = lazy(() => import("./pages/VerhaalCBR2026"));
const VerhaalTrudie = lazy(() => import("./pages/VerhaalTrudie"));
const VerhaalBuschauffeur = lazy(() => import("./pages/VerhaalBuschauffeur"));
const ParseExcel = lazy(() => import("./pages/ParseExcel"));
const TaxiOpleiders = lazy(() => import("./pages/TaxiOpleiders"));
const TaxiOpleiderDetail = lazy(() => import("./pages/TaxiOpleiderDetail"));
const TaxiAanmelden = lazy(() => import("./pages/TaxiAanmelden"));
const TweewielerOpleiders = lazy(() => import("./pages/TweewielerOpleiders"));
const TweewielerOpleiderDetail = lazy(() => import("./pages/TweewielerOpleiderDetail"));
const TweewielerAanmelden = lazy(() => import("./pages/TweewielerAanmelden"));
const TweewielerDashboard = lazy(() => import("./pages/TweewielerDashboard"));
const Vacatures = lazy(() => import("./pages/Vacatures"));
const VacaturePlaatsen = lazy(() => import("./pages/VacaturePlaatsen"));
const ProfielAanmaken = lazy(() => import("./pages/ProfielAanmaken"));
const Bijscholing = lazy(() => import("./pages/Bijscholing"));
const BijscholingAanmelden = lazy(() => import("./pages/BijscholingAanmelden"));
const InstructeurOpleiders = lazy(() => import("./pages/InstructeurOpleiders"));
const OpleiderAanmelden = lazy(() => import("./pages/OpleiderAanmelden"));
const OpleiderDetail = lazy(() => import("./pages/OpleiderDetail"));
const Kwaliteitskeurmerk = lazy(() => import("./pages/Kwaliteitskeurmerk"));
const TaxiOpleiderDashboard = lazy(() => import("./pages/TaxiOpleiderDashboard"));
const Ricky = lazy(() => import("./pages/Ricky"));
const PinterestApiDemo = lazy(() => import("./pages/PinterestApiDemo"));
const LocationDirectory = lazy(() => import("./pages/LocationDirectory"));

const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <GeoBlock>
        <BrowserRouter>
          <ScrollToTop />
          <FacebookPixel />
          <InactivityLogoutProvider />
          <AIAssistant />
          <CookieConsent />
          <Suspense fallback={<PageLoader />}>
            <AdSenseLoader />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/admin/*" element={<Admin />} />
              <Route path="/admin-recovery" element={<AdminRecovery />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/rijscholen" element={<AllRijscholen />} />
              <Route path="/rijscholen/kaart" element={<RijscholenKaart />} />
              <Route path="/rijschool/:slug" element={<RijschoolDetail />} />
              <Route path="/rijschool-aanmelden" element={<RijschoolAanmelden />} />
              <Route path="/rijschool-dashboard" element={<RijschoolDashboard />} />
              <Route path="/rijschool-leads" element={<RijschoolLeads />} />
              <Route path="/registratie-succes" element={<RegistratieSucces />} />
              <Route path="/online-theorie" element={<OnlineTheorie />} />
              <Route path="/boeken" element={<TheorieBoeken />} />
              <Route path="/theorie-boeken" element={<TheorieBoeken />} />
              <Route path="/videos" element={<Videos />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/nieuws" element={<Nieuws />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/algemene-voorwaarden" element={<Terms />} />
              <Route path="/over-ons" element={<OverOns />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/leerling-aanmelden" element={<LeerlingAanmelden />} />
              <Route path="/leerling-dashboard" element={<LeerlingDashboard />} />
              <Route path="/lead-bevestiging" element={<LeadConfirmation />} />
              <Route path="/lead-distributie" element={<LeadDistribution />} />
              <Route path="/kolb-test" element={<KolbTest />} />
              <Route path="/cbr" element={<CBR />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/partner-login" element={<PartnerAuth />} />
              <Route path="/partner-auth" element={<PartnerAuth />} />
              <Route path="/partner-dashboard" element={<PartnerDashboard />} />
              <Route path="/payment-confirmation" element={<PaymentConfirmation />} />
              <Route path="/subscription-upgrade" element={<SubscriptionUpgrade />} />
              <Route path="/verenigingen" element={<Verenigingen />} />
              <Route path="/vereniging-aanvragen" element={<VerenigingAanvragen />} />
              <Route path="/vereniging-login" element={<VerenigingAuth />} />
              <Route path="/vereniging-dashboard" element={<VerenigingDashboard />} />
              <Route path="/vereniging-betaalde-vermelding" element={<VerenigingBetaaldeVermelding />} />
              <Route path="/code95" element={<Code95Opleiders />} />
              <Route path="/code95/:slug" element={<Code95OpleiderDetail />} />
              <Route path="/code95-aanmelden" element={<Code95Aanmelden />} />
              <Route path="/code95-login" element={<Code95Login />} />
              <Route path="/code95-dashboard" element={<Code95OpleiderDashboard />} />
              <Route path="/code95-reset-password" element={<Code95ResetPassword />} />
              <Route path="/rij-instructeur" element={<RijInstructeur />} />
              <Route path="/cbr-examinator" element={<CBRExaminator />} />
              <Route path="/medewerker-theorie" element={<MedewerkerTheorie />} />
              <Route path="/verhaal/bram" element={<VerhaalBram />} />
              <Route path="/verhaal/nicolas" element={<VerhaalNicolas />} />
              <Route path="/verhaal/eva" element={<VerhaalEva />} />
              <Route path="/reviews/cbr-2026-de-grote-ommekeer" element={<VerhaalCBR2026 />} />
              <Route path="/verhaal/trudie" element={<VerhaalTrudie />} />
              <Route path="/verhaal/buschauffeur-bravo" element={<VerhaalBuschauffeur />} />
              <Route path="/parse-excel" element={<ParseExcel />} />
              <Route path="/taxi" element={<TaxiOpleiders />} />
              <Route path="/taxi/:slug" element={<TaxiOpleiderDetail />} />
              <Route path="/taxi-aanmelden" element={<TaxiAanmelden />} />
              <Route path="/tweewielers" element={<TweewielerOpleiders />} />
              <Route path="/tweewieler/:slug" element={<TweewielerOpleiderDetail />} />
              <Route path="/tweewieler-aanmelden" element={<TweewielerAanmelden />} />
              <Route path="/tweewieler-dashboard" element={<TweewielerDashboard />} />
              <Route path="/vacatures" element={<Vacatures />} />
              <Route path="/vacatures/plaatsen" element={<VacaturePlaatsen />} />
              <Route path="/vacatures/profiel-aanmaken" element={<ProfielAanmaken />} />
              <Route path="/vacatures/bijscholing" element={<Bijscholing />} />
              <Route path="/vacatures/bijscholing-aanmelden" element={<BijscholingAanmelden />} />
              <Route path="/vacatures/opleiders-instructeur-diploma" element={<InstructeurOpleiders />} />
              <Route path="/vacatures/opleider-aanmelden" element={<OpleiderAanmelden />} />
              <Route path="/vacatures/opleider/:slug" element={<OpleiderDetail />} />
              <Route path="/kwaliteitskeurmerk" element={<Kwaliteitskeurmerk />} />
              <Route path="/ricky" element={<Ricky />} />
              <Route path="/pinterest-api-demo" element={<PinterestApiDemo />} />
              <Route path="/:locationSlug" element={<LocationDirectory />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </GeoBlock>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
